import { omit, pick } from 'lodash';
import Client from '@lt/api';

import { addDays, addWeeks, format, nextSunday } from 'date-fns';
import { isValidUrl } from '../../../utils';

import type { getSearchEnqueue } from '@lt/api/dist/methods/search/types';
import type { DestinationOption } from '@lt/api/dist/methods/references/types';
import type { InitialValues } from '../../../components/Cigarette/context';
import type { SearchParamsWithDates, SearchParamsWithNights } from '../types';
import type { DepartureOption } from '@lt/api/dist/types/departure';
import type { StartDate } from '../../../models';
import { DEFAULT_KID_AGE } from '../../../pages/constants';

export const isValidHotelPath = (hotelUrl: string): boolean => {
  if (!isValidUrl(hotelUrl)) {
    return false;
  }

  const url = new URL(hotelUrl).pathname.split('/');
  return url.includes('hotels');
};

export const getHotelId = (hotelUrl: string): number | undefined => {
  try {
    if (!isValidHotelPath(hotelUrl)) {
      return;
    }

    const url = new URL(hotelUrl).pathname.split('/');
    return Number(url[url.length - 1].split('-')[0]);
  } catch (e) {
    console.error(e);
    return;
  }
};

export const getSearchParams = (
  widgetArgs: Record<string, any>
): Omit<getSearchEnqueue, 'to_city' | 'to_country' | 'start_date'> | undefined => {
  const hotelId = getHotelId(widgetArgs.hotel_url);

  if (!hotelId) {
    return;
  }

  const { departure, nights, adults, start_date, kids, search_type } = widgetArgs;
  const nightsRange = nights.split('..');
  const medianNights = (Number(nightsRange[1]) + Number(nightsRange[0])) / 2;

  const searchParams = {
    departure,
    nights: medianNights,
    adults,
    start_date,
    kids,
    ...(kids > 0 && { kids_ages: Array(kids).fill(DEFAULT_KID_AGE).toString() }),
    search_type,
  };

  return searchParams;
};

export const fetchHotelData = async (
  api: Client,
  hotelId: number
): Promise<Record<string, any> | undefined> =>
  api.getReferencesHotels({ hotel_ids: hotelId }) as any;

export const mapHotelToHotelContextState = (
  widgetParams: Record<string, any>
): Record<string, any> => {
  const { widgetSettings, searchParams } = widgetParams;

  return {
    searchParams,
    ...pickKeysFromWidgetSettings(widgetSettings),
  };
};

export const getCigaretteInitialParams = (
  hotelData: Record<string, any>,
  searchParams: Record<string, any>,
  departure: DepartureOption
): InitialValues => {
  const destination = {
    cityName: hotelData.city.name_ru,
    countryName: hotelData.country.name_ru,
    groupName: 'hotels',
    iso2: hotelData.country.iso2,
    label: hotelData.name,
    publicUrl: hotelData.public_url,
    value: hotelData.id,
  } as DestinationOption;

  const tourists = {
    adults: searchParams.adults,
    kids: Array(searchParams.kids).fill(DEFAULT_KID_AGE),
  };

  const startDate = new Date(searchParams.start_date);
  const endDate = addDays(startDate, searchParams.nights);

  const datepicker = {
    left: { date: startDate },
    right: {
      date: endDate,
    },
    shift: 0,
  };

  return {
    destination,
    tourists,
    datepicker,
    departure,
  };
};

export const pickKeysFromHotel = (hotelData: Record<string, any>) => ({
  ...pick(hotelData, [
    'id',
    'name',
    'stars',
    'public_url',
    'features',
    'city',
    'country',
    'rating',
    'images',
  ]),
  images: hotelData.images
    .slice(0, 10)
    .map((image: Record<string, string>) => ({ jpg: image.x900 })),
});

const pickKeysFromWidgetSettings = (widgetSettings: Record<string, any>) =>
  pick(widgetSettings, ['lang', 'redirect', 'utmParams']);

export const getStartDateByPreset = (presetName: StartDate | ''): Date => {
  const weekPresetToNum = {
    WEEK: 1,
    TWO_WEEKS: 2,
    THREE_WEEKS: 3,
  };
  const MonthAndDayPresetToObj = {
    FEB_14: { month: 2, day: 14 },
    FEB_23: { month: 2, day: 23 },
    MARCH_8: { month: 3, day: 8 },
    MARCH_28: { month: 3, day: 28 },
    MAY_1: { month: 5, day: 1 },
    MAY_9: { month: 5, day: 9 },
    OCT_28: { month: 10, day: 28 },
    NOV_4: { month: 11, day: 4 },
  };

  switch (presetName) {
    case 'WEEK':
    case 'TWO_WEEKS':
    case 'THREE_WEEKS': {
      const weeksCount = weekPresetToNum[presetName];

      return addWeeks(new Date(), weeksCount);
    }
    case 'NEXT_WEEKEND':
      return nextSunday(new Date());
    case 'FEB_14':
    case 'FEB_23':
    case 'MARCH_8':
    case 'MARCH_28':
    case 'MAY_1':
    case 'MAY_9':
    case 'OCT_28':
    case 'NOV_4': {
      const { month, day } = MonthAndDayPresetToObj[presetName];
      const curDate = new Date();
      const date = new Date(curDate.getFullYear(), month - 1, day);

      if (date < curDate) {
        date.setFullYear(date.getFullYear() + 1);
      }

      return date;
    }
    case 'NEW_YEAR':
      return new Date(new Date().getFullYear() + 1, 0, 1);
    default:
      return addWeeks(new Date(), 2);
  }
};

export const convertNightsToDatesParams = (
  searchParamsWithNights: SearchParamsWithNights,
  isMobile: boolean
): SearchParamsWithDates => {
  const startDate =
    searchParamsWithNights.start_date instanceof Date
      ? searchParamsWithNights.start_date
      : getStartDateByPreset(searchParamsWithNights.start_date);
  const startDateString = formatDateToDots(startDate);

  const endDateMedian = addDays(startDate, searchParamsWithNights.nights);
  const endDateMedianString = formatDateToDots(endDateMedian);
  const endDateFrom = addDays(startDate, searchParamsWithNights.nights - 1);
  const endDateTill = addDays(startDate, searchParamsWithNights.nights + 1);

  const { hotel_id } = searchParamsWithNights;

  return omit(
    {
      ...searchParamsWithNights,
      hotel_ids: hotel_id,
      start_date_primary: startDateString,
      start_date_from: startDateString,
      start_date_till: startDateString,
      end_date_primary: endDateMedianString,
      end_date_from: !isMobile ? formatDateToDots(endDateFrom) : endDateMedianString,
      end_date_till: !isMobile ? formatDateToDots(endDateTill) : endDateMedianString,
    },
    ['start_date', 'nights', 'departure', 'hotel_id']
  ) as SearchParamsWithDates;
};

export const formatDateToDots = (date: Date): string => format(date, 'dd.MM.yyyy');
